import React, { useState } from 'react';
import { Button, Table, Input, Card, CardBody } from 'reactstrap';
import { PaymentMethod } from '../types';
import { formatDecimal } from '../../../utils/numberUtils';

interface Props {
    paymentMethods: PaymentMethod[];
    paymentMethodsList: any[];
    onAdd: () => void;
    onRemove: (id: string) => void;
    onUpdate: (id: string, field: keyof PaymentMethod, value: any) => void;
    totalToPay: number;
    paymentsTotal: number;
}

const PaymentMethods = ({ paymentMethods, paymentMethodsList, onAdd, onRemove, onUpdate, totalToPay, paymentsTotal }: Props) => {
    
    const [errorState] = useState<{ id: string, field: string, message: string } | null>(null);
    const difference = totalToPay - paymentsTotal;
    const isDifference = Math.abs(difference) > 0.009; // Tolerancia para errores de redondeo

    const handleNumberChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        id: string,
        field: keyof PaymentMethod
    ) => {

        let value = e.target.value;

        value = value.replace(',', '.');

        const regex = /^\d*\.?\d{0,2}$/;

        if (!regex.test(value)) {
            return;
        }

        if (value === '') {
            onUpdate(id, field, '');
            return;
        }

        const numberValue = parseFloat(value);

        if (!isNaN(numberValue) && numberValue >= 0) {
            onUpdate(id, field, value);
        }
    };


    return (
        <Card className="shadow border-0 overflow-hidden rounded-3 mb-4">
            <div className="bg-soft-primary p-3 border-bottom border-light d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0 text-primary fw-semibold d-flex align-items-center">
                    <i className="ri-bank-card-fill me-2 fs-18"></i>
                    Formas de Pago
                </h5>
                <Button color="primary" size="sm" onClick={onAdd}><i className="ri-add-line me-1"></i> Añadir</Button>
            </div>
      <CardBody className="p-0 bg-white">
        <div className="table-responsive">
          <Table className="table-nowrap align-middle mb-0 table-hover">
            <thead className="bg-light text-muted text-uppercase small">
                        <tr>
                            <th>Forma</th>
                            <th style={{ width: '100px' }}>Valor<span className="text-danger">*</span></th>
                            <th style={{ width: '80px' }}>Plazo<span className="text-danger">*</span></th>
                            <th style={{ width: '100px' }}>Tiempo</th>
                            <th style={{ width: '40px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {paymentMethods.map((method, index) => (
                            <tr key={method.id} className={index % 2 === 0 ? 'bg-white' : 'bg-soft-light bg-opacity-25'}>
                                <td>
                                    <Input type="select" value={method.methodId} onChange={(e) => onUpdate(method.id, "methodId", e.target.value)} bsSize="sm">
                                        {paymentMethodsList.map((pm: any) => (<option key={pm.id} value={pm.id}>{pm.name}</option>))}
                                    </Input>
                                </td>
                                <td>
                                    <Input 
                                        type="text" 
                                        inputMode="decimal"
                                        value={method.total} 
                                        onChange={(e) => handleNumberChange(e, method.id, "total")} 
                                        bsSize="sm" 
                                        invalid={errorState?.id === method.id && errorState?.field === "total"}
                                    />
                                </td>
                                <td>
                                    <Input 
                                        type="text" 
                                        inputMode="decimal"
                                        value={method.term} 
                                        onChange={(e) => handleNumberChange(e, method.id, "term")} 
                                        bsSize="sm" 
                                        invalid={errorState?.id === method.id && errorState?.field === "term"}
                                    />
                                </td>
                                <td>
                                    <Input type="select" value={method.timeUnit} onChange={(e) => onUpdate(method.id, "timeUnit", e.target.value)} bsSize="sm">
                                        <option value="dias">Días</option>
                                        <option value="meses">Meses</option>
                                        <option value="anios">Años</option>
                                    </Input>
                                </td>
                                <td>
                                    <Button 
                                        color="soft-danger" 
                                        size="sm" 
                                        className="btn-icon shadow-none" 
                                        onClick={() => onRemove(method.id)}
                                        title="Eliminar forma de pago"
                                    >
                                        <i className="ri-delete-bin-line"></i>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
            {errorState && <div className="alert alert-danger mx-4 my-3">{errorState.message}</div>}
            {totalToPay > 0 && isDifference && (
                <div className={`alert ${difference > 0 ? 'alert-warning' : 'alert-danger'} mx-4 mb-3 mt-3`}>
                    <div className="d-flex align-items-center">
                        <i className={`ri-${difference > 0 ? 'alert' : 'error-warning'}-line me-2 fs-5`}></i>
                        <div>
                            Tiene una diferencia de: <strong>${formatDecimal(Math.abs(difference))}</strong>
                            {difference > 0 ? ' que falta por asignar.' : ' que excede el total.'}
                        </div>
                    </div>
                </div>
            )}
            </CardBody>
        </Card>
    );
};

export default PaymentMethods;
